import React from 'react';
import { PropertyAddressFormRow } from './types';
import { CategoryDropdown } from '../category';
import { AddressSearchSection } from '../addresses';
import { Address } from '../addresses/types';
import '../../../components/common/CRMForm.css';

interface PropertyAddressSectionProps {
  propertiesAddresses: PropertyAddressFormRow[];
  onChange: (propertiesAddresses: PropertyAddressFormRow[]) => void;
}

const rowToSelectedAddress = (row: PropertyAddressFormRow): Address | null => {
  if (!row.addressId) return null;
  return {
    id: row.addressId,
    street1: row.street1 || '',
    street2: row.street2,
    unit: row.unit,
    city: row.city || '',
    state: row.state || '',
    zip: row.zip || '',
    country: row.country || 'US',
    verified: false,
    dateCreated: '',
    dateUpdated: '',
    whoUpdated: ''
  };
};

const PropertyAddressSection: React.FC<PropertyAddressSectionProps> = ({
  propertiesAddresses,
  onChange
}) => {
  const addAddress = () => {
    const newRow: PropertyAddressFormRow = {
      id: 0,
      addressId: 0,
      categoryId: 0,
      isPrimary: propertiesAddresses.length === 0
    };
    onChange([...propertiesAddresses, newRow]);
  };

  const updateRow = (index: number, field: keyof PropertyAddressFormRow, value: unknown) => {
    let updated = propertiesAddresses.map((row, i) => {
      if (i !== index) {
        if (field === 'isPrimary' && value) return { ...row, isPrimary: false };
        return row;
      }
      return { ...row, [field]: value };
    });
    if (field === 'isPrimary' && value) {
      updated = updated.map((row, i) => ({ ...row, isPrimary: i === index }));
    }
    if (field === 'isPrimary' && !value) {
      const hasPrimary = updated.some(r => r.isPrimary);
      if (!hasPrimary && updated.length > 0) updated[0] = { ...updated[0], isPrimary: true };
    }
    onChange(updated);
  };

  const handleAddressSelect = (index: number, address: Address | null) => {
    const updated = propertiesAddresses.map((row, i) => {
      if (i !== index) return row;
      if (!address) {
        return {
          ...row,
          addressId: 0,
          street1: undefined,
          street2: undefined,
          unit: undefined,
          city: undefined,
          state: undefined,
          zip: undefined,
          country: undefined
        };
      }
      return {
        ...row,
        addressId: address.id,
        street1: address.street1,
        street2: address.street2,
        unit: address.unit,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country || 'US'
      };
    });
    onChange(updated);
  };

  const removeAddress = (index: number) => {
    const updated = propertiesAddresses.filter((_, i) => i !== index);
    const removed = propertiesAddresses[index];
    if (removed?.isPrimary && updated.length > 0) {
      updated[0] = { ...updated[0], isPrimary: true };
    }
    onChange(updated);
  };

  return (
    <div className="form-section-content">
      {propertiesAddresses.map((row, index) => (
        <div key={row.id || `temp-${index}`} className="form-item-group">
          <div className="app-form-group">
            <label className="app-form-label required">Property Category</label>
            <CategoryDropdown
              parentType="property"
              value={row.categoryId}
              onChange={(value) => updateRow(index, 'categoryId', value)}
              required={true}
              className={row.categoryId === 0 ? 'error' : ''}
            />
            {row.categoryId === 0 && (
              <div className="app-form-error">Please select a property category</div>
            )}
          </div>

          <div className="app-form-group">
            <label className="app-form-label">
              <input
                type="checkbox"
                checked={row.isPrimary}
                onChange={(e) => updateRow(index, 'isPrimary', e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Primary address
            </label>
          </div>

          <div className="app-form-group">
            <AddressSearchSection
              selectedAddress={rowToSelectedAddress(row)}
              onAddressSelect={(addr) => handleAddressSelect(index, addr)}
              label="Search Address"
              placeholder="Search addresses by street, city, state, or zip..."
              availableForProperty={false}
              hideSelectedRemoveButton
            />
            {(!row.addressId || row.addressId === 0) && (
              <div className="app-form-error">Please search and select an address</div>
            )}
          </div>

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn-delete"
              onClick={() => removeAddress(index)}
              title="Remove address"
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        className="app-btn app-btn-secondary"
        onClick={addAddress}
      >
        Add Address
      </button>

      {propertiesAddresses.length > 0 && !propertiesAddresses.some(a => a.isPrimary) && (
        <div className="form-warning">
          No primary address selected. Please mark one as primary.
        </div>
      )}
    </div>
  );
};

export default PropertyAddressSection;
